import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import { isCalendarUrlOrEmpty } from "@pima/utils";
import * as S from "effect/Schema";
import { ClientNotFound, ContractType } from "./admin-clients.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { EmailTaken, InvalidUserInput } from "./session.js";
import { Forbidden } from "./shares.js";
import { InvalidShareRequest } from "./sharing.js";
import { AccountEmailSchema, LanguageSchema, UserLanguageSchema, } from "./column-schemas.js";
/**
 * A `yp_users` row addressed by id/email for admin user management —
 * deliberately narrower than `employees.EmployeeRow` (the `vw_user_metadata`
 * join): these intents manage the account row itself (role, status, client
 * membership) across any client the caller may manage, not one client's
 * employee-list table.
 */
export const AdminUserRow = S.Struct({
    id: S.UUID,
    email: S.String,
    first_name: S.NullOr(S.String),
    last_name: S.NullOr(S.String),
    language: UserLanguageSchema,
    client_id: S.NullOr(S.UUID),
    role: S.NullOr(S.UUID),
    status: S.String,
    additional_roles: S.NullOr(S.String),
    email_notifications: S.NullOr(S.Boolean),
    last_access: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    contract_types: S.Array(ContractType),
    /**
     * The admin-management screen's free-form profile fields (`{
     * profile_picture, calendar, official_title }`) — opaque here, matching
     * every other jsonb `metadata` column in this contract.
     */
    metadata: S.NullOr(S.Unknown),
});
/**
 * The admin-management screen's own list — internal-role accounts
 * (ohs-admin/sifa/ama/external-sifa) across every client, unlike
 * `employees.listEmployees` (one client's roster). `role_name` rides
 * alongside via a `yp_roles` join rather than on `AdminUserRow` itself: the
 * get/update/invite endpoints return straight from an INSERT/UPDATE
 * `RETURNING`, which can't join.
 */
export const AdminUserListRow = S.extend(AdminUserRow, S.Struct({ role_name: S.NullOr(S.String) }));
/** Comma-joined, matching `employees.listEmployees`'s own `role_ids`/`location_ids` UrlParams encoding for a UUID array. */
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
export const ListAdminUsersParams = S.extend(PagingParams, S.Struct({
    search: S.optional(S.NonEmptyString),
    role_ids: S.optional(UuidCsvList),
    /**
     * Narrows the cross-client roster to one client's seat holders — the
     * add-client wizard's licenses screen lists OHS officers/location
     * managers by their account role and invitation status. Directory
     * membership is maintained separately by the shared membership writer.
     */
    client_id: S.optional(S.UUID),
}));
export const ListAdminUsers = HttpApiEndpoint.get("listAdminUsers", "/admin/users")
    .setUrlParams(ListAdminUsersParams)
    .addSuccess(pagedResponse(AdminUserListRow))
    .addError(Forbidden, { status: 403 });
export const GetUserByEmailPayload = S.Struct({ email: S.NonEmptyString });
/**
 * Email is globally unique on `yp_users`, so this is a nullable single-row
 * lookup rather than a list — the add-client/invite screens use it purely as
 * a pre-flight collision check ahead of `inviteUser`.
 */
export const GetUserByEmail = HttpApiEndpoint.post("getUserByEmail", "/admin/users/get-by-email")
    .setPayload(GetUserByEmailPayload)
    .addSuccess(S.Struct({ data: S.NullOr(AdminUserRow) }))
    .addError(Forbidden, { status: 403 });
export const AdminUserNotFound = S.Struct({
    _tag: S.Literal("AdminUserNotFound"),
});
export const GetUserPayload = S.Struct({ id: S.UUID });
export const GetUser = HttpApiEndpoint.post("getUser", "/admin/users/get")
    .setPayload(GetUserPayload)
    .addSuccess(S.Struct({ data: AdminUserRow }))
    .addError(AdminUserNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * Free-form profile fields the admin-management and add-client-wizard
 * screens collect alongside the account row itself — one shape shared by
 * invite and update, so a value refused on create cannot slip in on a
 * later edit.
 */
/** An http(s) link or empty — what the dashboard's contact box can open. */
const CalendarLink = S.String.pipe(S.filter((value) => isCalendarUrlOrEmpty(value), {
    message: () => "calendar must be an http(s) link or empty",
}));
const UserMetadataFields = {
    profile_picture: S.optional(S.String),
    // `null` clears the link the same as `""` or whitespace; the key being
    // absent from the object leaves whatever is already stored untouched.
    calendar: S.optional(S.NullOr(CalendarLink)),
    official_title: S.optional(S.String),
};
export const UpdateUserMetadata = S.Struct(UserMetadataFields);
/**
 * `client_id`/`role` reassignment is restricted to unrestricted internal
 * roles at the handler level (an external SiFa may edit a user inside its
 * assigned clients but not move them out); every other field is open to any
 * caller this contract lets reach the row at all.
 */
export const UpdateUserPayload = S.Struct({
    id: S.UUID,
    email: S.optional(S.NonEmptyString),
    first_name: S.optional(S.NullOr(S.String)),
    last_name: S.optional(S.NullOr(S.String)),
    language: S.optional(LanguageSchema),
    status: S.optional(S.Literal("draft", "invited", "active", "suspended", "archived")),
    role: S.optional(S.UUID),
    client_id: S.optional(S.NullOr(S.UUID)),
    additional_roles: S.optional(S.NullOr(S.String)),
    contract_types: S.optional(S.Array(ContractType)),
    metadata: S.optional(S.NullOr(UpdateUserMetadata)),
});
export const UpdateUser = HttpApiEndpoint.post("updateUser", "/admin/users/update")
    .setPayload(UpdateUserPayload)
    .addSuccess(S.Struct({ data: AdminUserRow }))
    .addError(AdminUserNotFound, { status: 404 })
    .addError(EmailTaken, { status: 409 })
    .addError(Forbidden, { status: 403 });
/** Deleting the user would leave no holder of an `admin_access` role. */
export const LastAdminRemaining = S.Struct({
    _tag: S.Literal("LastAdminRemaining"),
});
export const DeleteUserPayload = S.Struct({ id: S.UUID });
export const DeleteUser = HttpApiEndpoint.post("deleteUser", "/admin/users/delete")
    .setPayload(DeleteUserPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(AdminUserNotFound, { status: 404 })
    .addError(LastAdminRemaining, { status: 422 })
    .addError(Forbidden, { status: 403 });
export const CheckNotificationsSettingsPayload = S.Struct({
    client_id: S.UUID,
});
/**
 * Re-syncs every one of a client's `yp_users.email_notifications` to the
 * client's current `notifications_enabled` flag — the legacy
 * `GET /notifications/check/:clientid` bulk-write, run after an admin
 * flips the client-level toggle.
 */
export const CheckNotificationsSettings = HttpApiEndpoint.post("checkNotificationsSettings", "/admin/users/check-notifications")
    .setPayload(CheckNotificationsSettingsPayload)
    .addSuccess(S.Struct({ data: S.Struct({ updated: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const InviteUserMetadata = S.Struct(UserMetadataFields);
/**
 * One invite per call — the admin screens that call this contract always
 * invite a single named person, unlike the legacy endpoint's
 * `email | email[]` batch shape (used only by self-registration flows
 * outside this persona).
 */
export const InviteUserPayload = S.Struct({
    email: AccountEmailSchema,
    role: S.UUID,
    invite_url: S.optional(S.String),
    client_id: S.optional(S.NullOr(S.UUID)),
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    language: S.optional(LanguageSchema),
    contract_types: S.optional(S.Array(S.String)),
    metadata: S.optional(InviteUserMetadata),
});
/**
 * The invite JWT's target URL was rejected by
 * `USER_INVITE_URL_ALLOW_LIST` — the same guard the legacy
 * `UsersService.inviteUser` runs before minting a token, surfaced as a
 * typed error instead of a generic 500.
 */
export const InviteUrlNotAllowed = S.Struct({
    _tag: S.Literal("InviteUrlNotAllowed"),
});
/**
 * Creates an invited account or applies the requested client, role, and
 * profile assignment to an eligible existing draft/invited account before
 * enqueueing the invite mail. Unverified/active/archived/suspended targets and
 * rows owned by another client are left untouched with `invited: false`.
 */
export const InviteUser = HttpApiEndpoint.post("inviteUser", "/admin/users/invite")
    .setPayload(InviteUserPayload)
    .addSuccess(S.Struct({ data: S.Struct({ user: AdminUserRow, invited: S.Boolean }) }))
    .addError(EmailTaken, { status: 409 })
    .addError(InvalidUserInput, { status: 400 })
    .addError(InviteUrlNotAllowed, { status: 422 })
    .addError(Forbidden, { status: 403 });
/**
 * A seat holder's licence allocation, invitation and location scope as one
 * operation.
 *
 * The licences page used to send three requests — allocation, invite, then
 * locations — so a refused invite left the seat count already raised and a
 * failed location write left an invited account with no scope. The three
 * writes and the invitation mail now share one transaction: either the seat
 * exists, is filled, and is scoped, or nothing changed.
 *
 * `location_ids` may be empty, which means a deliberately unscoped member and
 * is preserved as such.
 */
export const InviteSeatHolderPayload = S.Struct({
    client_id: S.UUID,
    num_licenses_ohs_officers: S.NonNegativeInt,
    num_licenses_location_managers: S.NonNegativeInt,
    email: AccountEmailSchema,
    role: S.UUID,
    invite_url: S.optional(S.String),
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    location_ids: S.Array(S.UUID),
});
/**
 * The address already belongs to an account this flow may not re-invite — an
 * active, archived or suspended one, or a row owned by another client.
 * Unverified addresses are re-invited. The whole operation is refused rather
 * than reported as `invited: false`, so the seat allocation does not commit on
 * its own.
 */
export const SeatHolderNotInvitable = S.Struct({
    _tag: S.Literal("SeatHolderNotInvitable"),
});
export const InviteSeatHolder = HttpApiEndpoint.post("inviteSeatHolder", "/admin/users/invite-seat-holder")
    .setPayload(InviteSeatHolderPayload)
    .addSuccess(S.Struct({ data: S.Struct({ user: AdminUserRow }) }))
    .addError(EmailTaken, { status: 409 })
    .addError(InvalidUserInput, { status: 400 })
    .addError(SeatHolderNotInvitable, { status: 409 })
    .addError(ClientNotFound, { status: 404 })
    .addError(InviteUrlNotAllowed, { status: 422 })
    .addError(InvalidShareRequest, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const UpdateUserDearestAccessPayload = S.Struct({
    status: S.Literal("active", "suspended"),
    user_ids: S.Array(S.UUID),
});
/**
 * Suspends/restores a batch of users' Dearest access. Open to ohs-officers,
 * location-managers and admins, with no per-user client scoping.
 */
export const UpdateUserDearestAccess = HttpApiEndpoint.post("updateUserDearestAccess", "/admin/users/dearest-access")
    .setPayload(UpdateUserDearestAccessPayload)
    .addSuccess(S.Struct({ data: S.Struct({ updated: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const adminUsersGroup = HttpApiGroup.make("admin-users")
    .add(ListAdminUsers)
    .add(GetUserByEmail)
    .add(GetUser)
    .add(UpdateUser)
    .add(DeleteUser)
    .add(CheckNotificationsSettings)
    .add(InviteUser)
    .add(InviteSeatHolder)
    .add(UpdateUserDearestAccess)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-users.js.map